import type { FluxZapBatchTaskView } from "../types";

export type BatchTaskOutcomeKind =
  | "success"
  | "partial"
  | "failed"
  | "inProgress"
  | "unknown";

export interface BatchTaskOutcome {
  kind: BatchTaskOutcomeKind;
  /** Texto curto para chip. */
  label: string;
  /** Frase para tooltip ou dialog. */
  description: string;
  chipColor: "success" | "warning" | "error" | "info" | "default";
}

export type BatchTaskSendStatusKind = "error" | "inProgress" | "completed";

export interface BatchTaskSendStatus {
  kind: BatchTaskSendStatusKind;
  label: "Erro" | "Em andamento" | "Concluído";
  chipColor: "error" | "info" | "success";
}

function getBatchRecipientTotal(task: FluxZapBatchTaskView): number {
  const fromRefs = task.recipientReferences?.length ?? 0;
  if (fromRefs > 0) return fromRefs;
  const fromIds = task.recipientPatientIds.length;
  if (fromIds > 0) return fromIds;
  return task.requestedCount;
}

function hadSendAttempt(task: FluxZapBatchTaskView): boolean {
  return (
    getBatchRecipientTotal(task) > 0 ||
    task.totalProcessed > 0 ||
    task.requestedCount > 0
  );
}

function isIndeterminateDraftOrUnknown(task: FluxZapBatchTaskView): boolean {
  return (
    (task.status === "draft" || task.status === "unknown") &&
    task.successCount === 0 &&
    task.skippedCount === 0 &&
    task.totalProcessed === 0
  );
}

/** Deriva resultado legível para clínica a partir dos contadores do disparo. */
export function deriveBatchTaskOutcome(
  task: FluxZapBatchTaskView,
): BatchTaskOutcome {
  const total = getBatchRecipientTotal(task);

  if (task.status === "in-progress") {
    return {
      kind: "inProgress",
      label: "Em andamento",
      description:
        total > 0
          ? `O envio para ${formatBatchRecipientsColumn(total)} ainda está sendo processado.`
          : "O envio ainda está sendo processado.",
      chipColor: "info",
    };
  }

  if (isIndeterminateDraftOrUnknown(task)) {
    return {
      kind: "unknown",
      label: "Indeterminado",
      description: "O resultado deste envio ainda não está disponível.",
      chipColor: "default",
    };
  }

  if (task.status === "failed") {
    return {
      kind: "failed",
      label: "Nenhum enviado",
      description: "Nenhum paciente recebeu a mensagem.",
      chipColor: "error",
    };
  }

  if (task.successCount > 0 && task.skippedCount === 0) {
    return {
      kind: "success",
      label: "Todos enviados",
      description: "Todas as mensagens foram enviadas com sucesso.",
      chipColor: "success",
    };
  }

  if (task.successCount > 0 && task.skippedCount > 0) {
    return {
      kind: "partial",
      label: "Enviado parcialmente",
      description: `${task.successCount} de ${total} pacientes receberam a mensagem.`,
      chipColor: "info",
    };
  }

  if (task.successCount === 0 && hadSendAttempt(task)) {
    return {
      kind: "failed",
      label: "Nenhum enviado",
      description: "Nenhum paciente recebeu a mensagem.",
      chipColor: "error",
    };
  }

  return {
    kind: "unknown",
    label: "Indeterminado",
    description: "Não foi possível determinar o resultado deste envio.",
    chipColor: "default",
  };
}

/** Status simplificado para coluna do extrato (Erro / Em andamento / Concluído). */
export function deriveBatchTaskSendStatus(
  task: FluxZapBatchTaskView,
): BatchTaskSendStatus {
  if (task.status === "in-progress" || isIndeterminateDraftOrUnknown(task)) {
    return {
      kind: "inProgress",
      label: "Em andamento",
      chipColor: "info",
    };
  }

  if (task.status === "failed") {
    return { kind: "error", label: "Erro", chipColor: "error" };
  }

  if (task.successCount > 0) {
    return { kind: "completed", label: "Concluído", chipColor: "success" };
  }

  if (task.successCount === 0 && hadSendAttempt(task)) {
    return { kind: "error", label: "Erro", chipColor: "error" };
  }

  // draft/unknown com contadores parciais — tratar como andamento
  return {
    kind: "inProgress",
    label: "Em andamento",
    chipColor: "info",
  };
}

/** Ex.: "8", "0", "—" quando contadores ainda indeterminados. */
export function formatBatchReceivedTotal(task: FluxZapBatchTaskView): string {
  if (isIndeterminateDraftOrUnknown(task)) {
    return "—";
  }

  if (task.status === "in-progress" && getBatchRecipientTotal(task) <= 0) {
    return "—";
  }

  return `${task.successCount}`;
}

/** Ex.: "4", "0", "—" durante andamento ou contadores indeterminados. */
export function formatBatchFailuresCount(task: FluxZapBatchTaskView): string {
  if (task.status === "in-progress" || isIndeterminateDraftOrUnknown(task)) {
    return "—";
  }

  return `${task.skippedCount}`;
}

/** Ex.: "12 pacientes", "1 paciente", "Nenhum". */
export function formatBatchRecipientsColumn(count: number): string {
  if (count === 0) return "-";
  if (count === 1) return "1";
  return `${count}`;
}

/** Resumo curto para coluna da tabela (estilo relatório de campanha). */
export function formatBatchDeliverySummary(task: FluxZapBatchTaskView): string {
  const outcome = deriveBatchTaskOutcome(task);
  const total = getBatchRecipientTotal(task);
  const success = task.successCount;
  const skipped = task.skippedCount;

  if (outcome.kind === "inProgress") {
    return "Envio em andamento…";
  }

  if (outcome.kind === "unknown") {
    return "Resultado indisponível";
  }

  if (outcome.kind === "failed") {
    if (total > 0) {
      return `0 de ${total} receberam a mensagem`;
    }
    return "Nenhuma mensagem enviada";
  }

  let summary = `${success} de ${total} receberam a mensagem`;

  if (outcome.kind === "partial" && skipped > 0) {
    summary += `. ${skipped} não receberam`;
  }

  return summary;
}

/** Ratio compacto para coluna da tabela: (8/12), (0/5), etc. */
export function formatBatchDeliveryRatio(task: FluxZapBatchTaskView): string {
  const total = getBatchRecipientTotal(task);
  const success = task.successCount;
  const outcome = deriveBatchTaskOutcome(task);

  if (outcome.kind === "inProgress") return "(…)";
  if (outcome.kind === "unknown") return "(—)";
  if (total <= 0) return "(0/0)";

  return `(${success}/${total})`;
}

/** Frase única para o card de resumo do dialog de detalhe. */
export function formatBatchOutcomeDetailSentence(
  task: FluxZapBatchTaskView,
): string {
  const outcome = deriveBatchTaskOutcome(task);
  const total = getBatchRecipientTotal(task);
  const success = task.successCount;
  const skipped = task.skippedCount;
  const recipientsLabel = formatBatchRecipientsColumn(total);

  if (outcome.kind === "inProgress") {
    return total > 0
      ? `O envio para ${recipientsLabel} ainda está em andamento.`
      : "O envio ainda está em andamento.";
  }

  if (outcome.kind === "unknown") {
    return "Não foi possível determinar o resultado deste envio.";
  }

  if (outcome.kind === "failed") {
    return "Nenhum paciente recebeu a mensagem.";
  }

  if (outcome.kind === "success") {
    return total > 0
      ? `Todos os ${total} pacientes receberam a mensagem.`
      : "Todas as mensagens foram enviadas com sucesso.";
  }

  return `${success} de ${total} pacientes receberam a mensagem.`;
}
